/**
 * @file Factory central de elementos do EP.
 * @module components/create/element
 *
 * Este arquivo concentra as opções transversais consumidas pelo pipeline
 * central. Componentes específicos documentam apenas suas opções próprias e
 * apontam para `Element#createElement` quando precisam referenciar o contrato
 * comum.
 */

import append from '../define/append';
import setAttributes from '../define/setAttributes';
import setEvents from '../define/setEvents';
import setIcon from '../define/setIcon';
import setMask from '../define/setMask';
import fieldbox, { appendFieldHelper } from './fieldbox';
import input from './input';
import { applyFullDefault } from './fullDefaults';
import { sliderTicks } from './slider';
import ajax from '../../tools/ajax';
import { ensureFontAwesome } from '../../tools/fontawesome';

let inputGroupCounter = 0;

/**
 * Implementação central da criação de elementos EP.
 *
 * @class
 */
class Element {
  /**
   * Detecta coordenadas de grid explícitas em filhos declarativos.
   *
   * @param {Object[]} [items=[]] Filhos declarativos.
   * @returns {boolean} `true` quando ao menos um filho possui coordenadas.
   */
  #hasAutoGridCoordinates(items = []) {
    if (!Array.isArray(items) || items.length === 0) return false;
    return items.some((item) => (
      item
      && typeof item === 'object'
      && (
        item.row !== undefined
        || item.col !== undefined
        || item.rowspan !== undefined
        || item.colspan !== undefined
      )
    ));
  }

  /**
   * Decide se o container de `content` deve usar CSS Grid automaticamente.
   *
   * `isGrid` explícito tem precedência. Na ausência dele, `cols`, `rows` ou
   * coordenadas nos filhos ativam o Grid.
   *
   * @param {Object} [options={}] Opções do container.
   * @returns {boolean} Decisão de Grid.
   * @see Element#createElement
   */
  #shouldUseAutoGrid(options = {}) {
    if (options.isGrid === true) return true;
    if (options.isGrid === false) return false;
    if (options.cols !== undefined || options.rows !== undefined) return true;
    return this.#hasAutoGridCoordinates(options.content);
  }

  /**
   * Resolve o limite de caracteres entre os aliases suportados.
   *
   * @param {Object} [options={}] Opções do elemento.
   * @returns {number|null} Inteiro positivo ou `null`.
   * @see Element#createElement
   */
  #normalizeMaxChars(options = {}) {
    const raw = options.maxchars ?? options.maxChars ?? options.maxlength ?? options.maxLength;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : null;
  }

  /**
   * Limpa HTML do Quill removendo blocos vazios artificiais nas extremidades e
   * limitando sequências internas de blocos vazios.
   *
   * @param {*} html HTML do editor.
   * @returns {string} HTML normalizado.
   */
  #normalizeQuillHtml(html) {
    const rawHtml = String(html || '').trim();
    if (!rawHtml) return '';

    const container = document.createElement('div');
    container.innerHTML = rawHtml;

    /**
     * Detecta um bloco `p`/`div` sem conteúdo significativo.
     *
     * @param {Node} node Nó candidato.
     * @returns {boolean} Estado de vazio.
     */
    const isEmptyBlockNode = (node) => {
      if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
      const tagName = String(node.tagName || '').toLowerCase();
      if (!['p', 'div'].includes(tagName)) return false;

      const clone = node.cloneNode(true);
      clone.querySelectorAll('br').forEach((br) => br.remove());
      const text = String(clone.textContent || '').replace(/\u00a0/g, ' ').trim();
      return text.length === 0 && clone.children.length === 0;
    };

    while (container.firstElementChild && isEmptyBlockNode(container.firstElementChild)) {
      container.firstElementChild.remove();
    }

    while (container.lastElementChild && isEmptyBlockNode(container.lastElementChild)) {
      container.lastElementChild.remove();
    }

    if (!container.children.length && !String(container.textContent || '').trim()) {
      return '';
    }

    let emptyRun = 0;
    Array.from(container.children).forEach((child) => {
      if (isEmptyBlockNode(child)) {
        emptyRun += 1;
        if (emptyRun > 2) {
          child.remove();
        }
        return;
      }

      emptyRun = 0;
    });

    return container.innerHTML.trim();
  }

  /**
   * Produz o texto do contador de caracteres.
   *
   * @param {number} [count=0] Quantidade atual.
   * @param {number|null} [maxChars=null] Limite.
   * @returns {string} Label do contador.
   */
  #getCharacterCountLabel(count = 0, maxChars = null) {
    return `${count} / ${maxChars} caracteres`;
  }

  /**
   * Cria ou reutiliza o contador de caracteres dentro de um wrapper.
   *
   * @param {HTMLElement} wrapper Wrapper do controle.
   * @param {number|null} [maxChars=null] Limite configurado.
   * @returns {HTMLElement|null} Contador.
   */
  #ensureCharacterCounter(wrapper, maxChars = null) {
    if (!wrapper) return null;

    let counter = wrapper.querySelector(':scope > .character-counter');
    if (!counter) {
      counter = document.createElement('div');
      counter.classList.add('character-counter');
      counter.setAttribute('aria-live', 'polite');
      wrapper.append(counter);
    }

    counter.dataset.maxchars = maxChars ? String(maxChars) : '';
    return counter;
  }

  /**
   * Atualiza texto e estado visual do contador.
   *
   * @param {HTMLElement} counter Contador.
   * @param {number} [count=0] Quantidade atual.
   * @param {number|null} [maxChars=null] Limite.
   * @returns {void}
   */
  #setCharacterCounterValue(counter, count = 0, maxChars = null) {
    if (!counter) return;

    counter.textContent = this.#getCharacterCountLabel(count, maxChars);
    counter.classList.toggle('over-limit', Boolean(maxChars && count > maxChars));
  }

  /**
   * Configura limite e contador de caracteres em Textarea.
   *
   * @param {HTMLTextAreaElement} elem Textarea.
   * @param {Object} [options={}] Opções; veja {@link Element#createElement}.
   * @returns {void}
   */
  #setupTextareaCharacterCounter(elem, options = {}) {
    if (!elem) return;

    const wrapper = elem.parentElement;
    const maxChars = this.#normalizeMaxChars(options);
    const shouldShow = options.showCharacterCount !== false && options.characterCount !== false;
    if (!maxChars) return;

    elem.maxLength = maxChars;
    elem.setAttribute('maxlength', String(maxChars));
    if (!shouldShow) return;

    wrapper?.classList.add('textarea');

    const counter = this.#ensureCharacterCounter(wrapper, maxChars);
    /** Atualiza e aplica o limite após input/change. */
    const update = () => {
      const nextValue = String(elem.value || '');
      if (nextValue.length > maxChars) {
        elem.value = nextValue.slice(0, maxChars);
      }
      this.#setCharacterCounterValue(counter, String(elem.value || '').length, maxChars);
    };

    elem.addEventListener('input', update);
    elem.addEventListener('change', update);
    update();
  }

  /**
   * Configura limite e contador de caracteres no Editor Quill.
   *
   * @param {Object} config Configuração de runtime.
   * @param {HTMLElement} config.elem Elemento do editor.
   * @param {HTMLElement} config.wrapper Wrapper FieldBox.
   * @param {HTMLTextAreaElement} config.hiddenField Campo persistível.
   * @param {Object} config.quill Instância Quill.
   * @param {Object} [config.options] Opções declarativas; veja {@link Element#createElement}.
   * @returns {void}
   */
  #setupEditorCharacterCounter({ elem, wrapper, hiddenField, quill, options = {} }) {
    if (!elem || !wrapper || !quill) return;

    const maxChars = this.#normalizeMaxChars(options);
    const shouldShow = options.showCharacterCount !== false && options.characterCount !== false;
    if (!maxChars) return;

    const counter = shouldShow ? this.#ensureCharacterCounter(wrapper, maxChars) : null;
    /** @returns {string} Texto corrente sem quebra final artificial. */
    const getText = () => String(quill.root?.innerText || quill.getText() || '').replace(/\n$/, '');
    /** Atualiza o contador com o texto corrente. */
    const update = () => {
      this.#setCharacterCounterValue(counter, getText().length, maxChars);
    };
    /**
     * Aplica o limite somente a alterações originadas pelo usuário.
     *
     * @param {*} delta Delta novo do Quill.
     * @param {*} oldDelta Delta anterior.
     * @param {string} source Origem da mudança.
     * @returns {void}
     */
    const enforceLimit = (delta, oldDelta, source) => {
      if (source !== 'user') {
        update();
        return;
      }

      const text = getText();
      if (text.length <= maxChars) {
        update();
        return;
      }

      quill.deleteText(maxChars, text.length - maxChars, 'silent');
      if (hiddenField) {
        const plainText = getText().trim();
        hiddenField.value = plainText.length > 0 ? this.#normalizeQuillHtml(quill.root.innerHTML) : '';
        hiddenField.dispatchEvent(new Event('input', { bubbles: true }));
      }
      update();
    };

    quill.on('text-change', enforceLimit);
    hiddenField?.addEventListener('input', update);
    update();
  }

  /**
   * Resolve strings no formato `dic.caminho.chave` contra `window.dic`.
   *
   * @param {*} value Valor possivelmente referenciado ao dicionário.
   * @returns {*} Valor localizado ou original quando não resolvido.
   */
  #resolveDicRef(value) {
    if (typeof value !== 'string') return value;
    const raw = value.trim();
    const unquoted = raw.replace(/^['"`\s]+|['"`\s]+$/g, '');
    if (!/^dic\./i.test(unquoted)) return value;

    const parts = unquoted.replace(/^dic\./i, '').split('.').filter(Boolean);
    let cursor = window?.dic;

    for (const part of parts) {
      if (!cursor || typeof cursor !== 'object') return value;
      if (Object.prototype.hasOwnProperty.call(cursor, part)) {
        cursor = cursor[part];
        continue;
      }
      const fallbackKey = Object.keys(cursor).find(
        (k) => String(k).toLowerCase() === part.toLowerCase()
      );
      if (!fallbackKey) return value;
      cursor = cursor[fallbackKey];
    }

    if (typeof cursor === 'string' || typeof cursor === 'number') {
      return String(cursor);
    }

    return value;
  }

  /**
   * Resolve referências `dic.*` nas opções de apresentação comuns.
   *
   * @param {Object} [options={}] Opções mutáveis.
   * @returns {Object} A mesma referência normalizada.
   * @see Element#createElement
   */
  #normalizeDisplayDicOptions(options = {}) {
    const next = options;
    ['label', 'placeholder', 'afterLabel', 'text', 'addText', 'removeText'].forEach((prop) => {
      if (typeof next[prop] === 'string') {
        next[prop] = this.#resolveDicRef(next[prop]);
      }
    });

    return next;
  }

  /**
   * Compara valor de opção de checkbox/radio com valor de formulário, incluindo
   * conversões boolean/string/number usadas pelos bindings atuais.
   *
   * @param {*} optionValue Valor da opção.
   * @param {*} formValue Valor vindo do binding.
   * @returns {boolean} Resultado da comparação.
   */
  #inputGroupValueMatches(optionValue, formValue) {
    if (formValue === undefined || formValue === null) return false;
    if (Array.isArray(formValue)) {
      return formValue.some((entry) => this.#inputGroupValueMatches(optionValue, entry));
    }
    if (optionValue === formValue) return true;
    if (typeof formValue === 'boolean') {
      if (optionValue === 'true') return formValue === true;
      if (optionValue === 'false') return formValue === false;
    }
    if (typeof optionValue === 'boolean') {
      if (formValue === 'true') return optionValue === true;
      if (formValue === 'false') return optionValue === false;
    }
    if (typeof formValue === 'number' && (optionValue === 'true' || optionValue === 'false')) {
      if (optionValue === 'true') return formValue === 1;
      if (optionValue === 'false') return formValue === 0;
    }
    return String(optionValue) === String(formValue);
  }

  /**
   * Normaliza a lista de ids vinculados de um InputGroup.
   *
   * @param {Object} [options={}] Opções do grupo; veja {@link Element#createInputGroup}.
   * @returns {string[]} Ids normalizados.
   */
  #normalizeInputGroupIds(options = {}) {
    const rawIds = Array.isArray(options.id)
      ? options.id
      : (options.id !== undefined && options.id !== null ? [options.id] : []);

    return rawIds
      .map((value, index) => {
        const raw = String(value || '').trim();
        if (raw) return raw;
        const base = String(options.name || 'input-group').trim() || 'input-group';
        return `${base}-${index + 1}`;
      })
      .filter(Boolean);
  }

  /**
   * Converte bindings comuns para boolean de checkbox.
   *
   * @param {*} value Valor recebido.
   * @returns {boolean} Boolean normalizado.
   */
  #coerceBoundBoolean(value) {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1';
    }
    return false;
  }

  /**
   * Resolve o id do container de InputGroup.
   *
   * @param {Object} [options={}] Opções do grupo.
   * @param {string[]} [fieldIds=[]] Ids vinculados.
   * @param {boolean} [usesBoundFields=false] Indica binding por campos individuais.
   * @returns {string} Id do grupo.
   */
  #createInputGroupId(options = {}, fieldIds = [], usesBoundFields = false) {
    if (typeof options.id === 'string' && options.id.trim().length > 0) {
      return options.id.trim();
    }

    inputGroupCounter += 1;
    const base = String(options.name || (usesBoundFields ? fieldIds.join('-') : '') || 'input-group')
      .trim()
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'input-group';

    return `${base}-${inputGroupCounter}`;
  }

  /**
   * Cria `element: 'inputGroup'` como conjunto de radio/checkbox.
   *
   * Quando há mais de 10 linhas calculadas, adiciona busca local e scroll.
   * Existe ainda um modo remoto legado que exige `product + category + domain +
   * module`; ele não usa `normalizeServiceTarget()` e deve ser tratado como
   * compatibilidade até migração explícita.
   *
   * @param {Object} options Configuração do grupo.
   * @param {'checkbox'|'radio'} [options.type='radio'] Tipo dos itens.
   * @param {'button'|'switch'|string} [options.appearance] Aparência dos itens.
   * @param {Array.<Object>} options.options Itens do grupo. Cada item pode definir label/text, value e icon.
   * @param {number|string} [options.cols=1] Colunas do grupo.
   * @param {number|string} [options.rows] Linhas explícitas do grupo.
   * @param {string|string[]} [options.id] Id do grupo ou ids dos campos vinculados.
   * @param {string} [options.name] Nome compartilhado quando não usa ids vinculados.
   * @param {*|Array<*>} [options.value] Valor/valores iniciais.
   * @param {*} [options.defaultValue] Fallback de valor.
   * @param {string} [options.key='main'] Chave usada pelo modo remoto legado.
   * @param {boolean} [options.loader=true] Loader da chamada remota legada.
   * @returns {Promise<null>} O método cria os elementos diretamente.
   * @see Element#createElement
   */
  async #createInputGroup(options) {
    applyFullDefault(options);
    if (options.full === undefined) {
      options.full = false;
    }

    const fieldIds = this.#normalizeInputGroupIds(options);
    const usesBoundFields = Array.isArray(options.id) && fieldIds.length > 0;
    const groupId = this.#createInputGroupId(options, fieldIds, usesBoundFields);
    const groupName = String(options.name || '').trim() || groupId;

    options.id = groupId;
    options.name = groupName;
    options.fieldIds = usesBoundFields ? fieldIds : null;

    if (options.value === undefined && options.defaultValue !== undefined) {
      options.value = options.defaultValue;
    }

    if(options.product && options.category && options.domain && options.module){
      const data = await ajax.post({
        product: options.product,
        category: options.category,
        domain: options.domain,
        module: options.module,
        method: 'getDataCombo',
        body: { key: options.key || 'main' },
        config: { loader: options?.loader !== false },
      });
      if(Array.isArray(data) && data.length > 0){
        options.options = data;
      }

      if(options.cols !== undefined && options.cols !== null && options.cols !== ''){
        if(!options.fbStyle){
          options.fbStyle = {};
        }
        options.fbStyle['grid-template-columns'] = `repeat(${options.cols}, 1fr)`;
      }
    }


    if (options.style === undefined) {
      options.style = {};
    }

    if (options.cols !== undefined && options.cols !== null && options.cols !== '') {
      if (typeof options.style === 'object') {
        options.style['grid-template-columns'] = `repeat(${options.cols}, 1fr)`;
      } else if (typeof options.style === 'string') {
        options.style += `grid-template-columns: repeat(${options.cols}, 1fr);`
      }
    }

    if (options.rows !== undefined && options.rows !== null && options.rows !== '') {
      if (typeof options.style === 'object') {
        options.style['grid-template-rows'] = `repeat(${options.rows}, auto)`;
      } else if (typeof options.style === 'string') {
        options.style += `grid-template-rows: repeat(${options.rows}, auto);`
      }
    }

    const inputGroupColumns = Math.max(1, Number(options.cols) || 1);
    const inputGroupRows = Math.ceil((Array.isArray(options.options) ? options.options.length : 0) / inputGroupColumns);
    const inputGroupNeedsScroll = inputGroupRows > 10;

    delete options.rows;
    delete options.cols;

    if (options.appearance === 'button') {
      if (options.class !== undefined && typeof options.class === 'string') {
        options.class += ' button-group item-group';
      } else {
        options.class = 'button-group item-group';
      }
    } else if (options.appearance === 'switch') {
      if (options.class !== undefined && typeof options.class === 'string') {
        options.class += ' input-group switch-group';
      } else {
        options.class = 'input-group switch-group';
      }
    } else {
      if (options.class !== undefined && typeof options.class === 'string') {
        options.class += ' input-group';
      } else {
        options.class = 'input-group';
      }
    }

    if (options.full === true && !options.class.split(/\s+/).includes('full')) {
      options.class += ' full';
    }

    if (inputGroupNeedsScroll) {
      options.class += ' input-group-scrollable';
    }

    const types = ['checkbox', 'radio'];
    const item = (options.type && types.includes(options.type)) ? (options.type) : ('radio');
    options.parent = fieldbox(options);
    options.element = 'div';
    options.tag = 'div';

    const gridBox = await createElement(options);
    // Labels must target this group's inputs even when another dialog stays mounted.
    const optionInstanceId = ++inputGroupCounter;

    if (inputGroupNeedsScroll) {
      const search = document.createElement('input');
      const filterPlaceholder = window.dic?.labels?.filterOptions || window.dic?.labels?.search || '';
      search.type = 'search';
      search.className = 'input-group-search';
      search.placeholder = filterPlaceholder;
      search.setAttribute('aria-label', filterPlaceholder);
      search.addEventListener('input', () => {
        const term = search.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
        gridBox.querySelectorAll(':scope > label').forEach((label) => {
          const matches = !term || label.textContent
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLocaleLowerCase()
            .includes(term);
          const option = document.getElementById(label.htmlFor);
          label.hidden = !matches;
          if (option) option.hidden = !matches;
        });
      });
      gridBox.before(search);
    }

    for (const i in options.options) {
      const opt = options.options[i];
      const optionLabel = opt.label ?? opt.text;
      const optionValue = opt.value !== undefined ? opt.value : optionLabel;
      const optionId = usesBoundFields ? (fieldIds[i] || `${groupId}-${Number(i) + 1}`) : `${groupId}-${optionInstanceId}-${Number(i) + 1}`;
      const inputName = usesBoundFields ? optionId : groupName;
      const presetValue = usesBoundFields && Array.isArray(options.value)
        ? options.value[i]
        : options.value;
      const value = usesBoundFields && item === 'checkbox'
        ? 'on'
        : optionValue;

      let checked = false;
      if (presetValue !== undefined) {
        checked = usesBoundFields
          ? this.#coerceBoundBoolean(presetValue)
          : (
            typeof presetValue === 'boolean'
              ? presetValue
              : this.#inputGroupValueMatches(optionValue, presetValue)
          );
      }

      await createElement({
        element: item,
        parent: gridBox,
        appearance: options.appearance,
        hasFieldBox: false,
        id: optionId,
        name: inputName,
        text: optionLabel,
        icon: opt.icon,
        value: value,
        checked: checked
      });
    }

    return null;
  }

  /**
   * Cria o label associado a Checkbox/Radio e aplica aparência switch/button.
   *
   * @param {HTMLInputElement} elem Input criado.
   * @param {Object} options Opções; veja {@link Element#createElement}.
   * @returns {Promise<void>}
   */
  async #setCheckRadio(elem, options) {
    const label = document.createElement('label');
    label.setAttribute('for', options.id);

    if (options.text !== undefined && typeof options.text === 'string') {
      if (options.appearance === 'switch') {
        const span1 = document.createElement('span');
        span1.classList.add('switch-toggle');
        const span2 = document.createElement('span');
        span2.classList.add('switch-text');
        if (options.icon && typeof options.icon === 'object') {
          setIcon(span2, { icon: options.icon });
        }
        span2.append(document.createTextNode(options.text));
        label.append(span1, span2);
      } else if (options.appearance === 'button') {
        const span = document.createElement('span');
        span.classList.add('bt-default');
        if (options.icon && typeof options.icon === 'object') {
          setIcon(span, { icon: options.icon });
        }
        span.append(document.createTextNode(options.text));
        label.append(span);
      } else {
        if (options.icon && typeof options.icon === 'object') {
          setIcon(label, { icon: options.icon });
        }
        label.append(document.createTextNode(options.text));
      }
      delete options.text;
      elem.after(label);
    }
  }

  /**
   * Preenche um elemento de lista a partir de strings ou configurações EP.
   *
   * @param {HTMLElement} elem Lista alvo.
   * @param {Object} options Opções com `list`.
   * @returns {Promise<void>}
   */
  async #setList(elem, options) {
    if (options.list !== undefined && Array.isArray(options.list) && options.list.length > 0) {
      for (const i in options.list) {
        const l = options.list[i];
        const li = document.createElement('li');
        elem.append(li);

        if (typeof l === 'object') {
          const inner = await create(l);
          li.append(inner);
        } else {
          li.textContent = l;
        }
      }
    }
  }

  /**
   * Cria um elemento EP a partir da configuração já resolvida por `create()`.
   *
   * A factory roteia componentes especializados, aplica append, atributos,
   * máscara, eventos, ícones e pós-processamentos específicos. Também cria
   * recursivamente `content` e calcula Grid automático quando necessário.
   *
   * @param {Object} options Configuração declarativa resolvida.
   * @param {string} options.element Nome declarativo do componente/elemento.
   * @param {string} [options.tag] Tag HTML usada quando não existe factory específica.
   * @param {HTMLElement|string} [options.parent] Elemento pai ou seletor; em `create()` também pode ser `:ident`.
   * @param {'before'|'after'|'prepend'|'append'|string} [options.append] Estratégia de inserção relativa.
   * @param {string} [options.ident] Identificador lógico usado por `create()` para referências posteriores.
   * @param {string|string[]} [options.id] Id HTML; alguns composites também aceitam lista de ids.
   * @param {string|string[]} [options.class] Classes CSS.
   * @param {Object|string} [options.style] Estilo inline.
   * @param {Object|string} [options.fbStyle] Estilo aplicado ao FieldBox quando suportado.
   * @param {boolean} [options.full] Ocupação de largura conforme o componente.
   * @param {boolean} [options.hasFieldBox] Habilita/desabilita o wrapper FieldBox quando aplicável.
   * @param {string|false} [options.label] Label principal.
   * @param {'top'|'left'|'right'|'bottom'|string} [options.labelPosition] Posição do label quando suportada.
   * @param {string} [options.afterLabel] Texto auxiliar após o controle.
   * @param {Object|string|false} [options.prefix] Prefixo visual/funcional.
   * @param {Object|string|false} [options.suffix] Sufixo visual/funcional.
   * @param {Object} [options.icon] Configuração de ícone.
   * @param {string|Object} [options.tooltip] Tooltip.
   * @param {Object<string,*>} [options.aria] Atributos ARIA.
   * @param {Object<string,*>} [options.data] Dataset.
   * @param {Object<string,*>} [options.proto] Propriedades adicionadas ao elemento quando suportadas.
   * @param {number|string} [options.row] Linha no grid externo.
   * @param {number|string} [options.col] Coluna no grid externo.
   * @param {number|string} [options.rowspan] Span vertical.
   * @param {number|string} [options.colspan] Span horizontal.
   * @param {boolean} [options.isGrid] Força/desabilita Grid para `content`.
   * @param {number|string} [options.cols] Colunas do Grid interno.
   * @param {number|string} [options.rows] Linhas do Grid interno.
   * @param {string|number} [options.text] Conteúdo textual.
   * @param {string} [options.html] Conteúdo HTML quando suportado.
   * @param {Object[]} [options.content] Filhos declarativos.
   * @param {*} [options.value] Valor inicial.
   * @param {*} [options.defaultValue] Valor fallback.
   * @param {string} [options.name] Nome de campo quando aplicável.
   * @param {string} [options.placeholder] Placeholder.
   * @param {boolean} [options.required] Obrigatoriedade.
   * @param {boolean} [options.disabled] Desabilitação.
   * @param {boolean} [options.readOnly] Somente leitura.
   * @param {boolean} [options.readonly] Alias aceito por componentes compatíveis.
   * @param {Object|string} [options.mask] Configuração consumida por `setMask()`.
   * @param {number} [options.maxchars] Limite de caracteres para Textarea/Editor.
   * @param {number} [options.maxChars] Alias de `maxchars`.
   * @param {number} [options.maxlength] Alias de `maxchars`.
   * @param {number} [options.maxLength] Alias de `maxchars`.
   * @param {boolean} [options.showCharacterCount=true] Exibe contador quando existe limite.
   * @param {boolean} [options.characterCount=true] Alias de controle do contador.
   * @param {Object|Array|Function|string} [options.options] Fonte de opções em componentes que a consomem.
   * @param {Object|string|Array} [options.dependsOn] Dependências declarativas quando suportadas.
   * @param {Function} [options.click] Callback de click.
   * @param {Function} [options.change] Callback de change.
   * @param {Function} [options.input] Callback de input.
   * @param {Function} [options.blur] Callback de blur.
   * @param {Function} [options.focus] Callback de focus.
   * @param {Function} [options.keydown] Callback de keydown.
   * @param {Function} [options.keyup] Callback de keyup.
   * @param {Function} [options.mousedown] Callback de mousedown.
   * @param {Function} [options.mouseup] Callback de mouseup.
   * @returns {Promise<HTMLElement|*>} Elemento criado ou resultado do componente especializado.
   * @throws {Error} Quando um módulo especializado obrigatório é inválido, como DataGrid sem factory.
   * @see module:components/ep
   * @see setAttributes
   * @see setEvents
   * @see setMask
   */
  async createElement(options) {
    options = this.#normalizeDisplayDicOptions(options);

    if (
      options?.key
      && ['chart', 'fastchart', 'miniDisplay', 'metricDisplay'].includes(options.element)
    ) {
      const { resolveDataReference } = await import('./dataReference');
      const dashboardRuntime = options.__dashboardContext || null;
      const resolvedReference = await resolveDataReference(options, {
        advancedFilter: Array.isArray(dashboardRuntime?.filters)
          ? dashboardRuntime.filters
          : [],
        compatibleFilters: Boolean(dashboardRuntime),
        cache: dashboardRuntime?.dataReferenceCache || null,
      });
      options = {
        ...options,
        data: resolvedReference.data,
      };
      if (typeof dashboardRuntime?.registerDataReference === 'function') {
        dashboardRuntime.registerDataReference({
          element: options.element,
          key: options.key,
          filterCompatibility: resolvedReference.filterCompatibility,
        });
      }
    }
    const classNames = String(options?.class || '').trim();
    const fontAwesomeFamily = (
      /(?:^|\s)fa-solid(?:\s|$)|(?:^|\s)fas(?:\s|$)/.test(classNames) ? 'solid'
        : /(?:^|\s)fa-regular(?:\s|$)|(?:^|\s)far(?:\s|$)/.test(classNames) ? 'regular'
          : /(?:^|\s)fa-brands(?:\s|$)|(?:^|\s)fab(?:\s|$)/.test(classNames) ? 'brands'
            : /(?:^|\s)fa-light(?:\s|$)|(?:^|\s)fal(?:\s|$)/.test(classNames) ? 'light'
              : /(?:^|\s)fa-duotone(?:\s|$)|(?:^|\s)fad(?:\s|$)/.test(classNames) ? 'duo'
                : undefined
    );
    const needsFontAwesome = (
      classNames.includes('fa-')
      || options?.element === 'i'
      || options?.tag === 'i'
    );

    if (needsFontAwesome) {
      await ensureFontAwesome(fontAwesomeFamily);
    }

    let elem;
    switch (options.element) {
      case 'cep':
      case 'checkbox':
      case 'cnpj':
      case 'color':
      case 'cpf':
      case 'creditCard':
      case 'currency':
      case 'date':
      case 'email':
      case 'hidden':
      case 'integer':
      case 'float':
      case 'password':
      case 'phone':
      case 'radio':
      case 'rg':
      case 'search':
      case 'slider':
      case 'spinner':
      case 'switch':
      case 'text':
      case 'time':
      case 'url':
        [elem, options] = await input(options);
        break;
      case 'boardCardActivity':
        const { default: cardActivity } = await import('./board/cardActivity');
        [elem, options] = await cardActivity(elem, options);
        break;
      case 'autoform':
        const { default: autoform } = await import('./autoform');
        await autoform(options);
        break;
      case 'address':
        const { default: address } = await import('./address');
        address(options);
        break;
      case 'button':
        const { default: button } = await import('./button');
        [elem, options] = button(options);
        break;
      case 'datetime':
        const { default: datetime } = await import('./datetime/datetime');
        await datetime(options);
        break;
      case 'period':
        const { default: period } = await import('./period');
        await period(options);
        break;
      case 'audio':
      case 'video':
        const { media } = await import('./media');
        [elem, options] = await media(elem, options);
        break;
      case 'dashboard':
        const { default: dashboard } = await import('./dashboard');
        [elem, options] = await dashboard(options);
        break;
      case 'report':
        const { default: report } = await import('./report');
        [elem, options] = await report(options);
        break;
      case 'chart':
      case 'fastchart':
        const { default: chart } = await import('./chart/chart');
        [elem, options] = chart(options);
        break;
      case 'combo':
        const { default: combo } = await import('./combo');
        [elem, options] = await combo(options);
        break;
      case 'tagbox':
        const { default: tagbox } = await import('./tagbox');
        [elem, options] = await tagbox(options);
        break;
      case 'colorPicker':
        const { default: colorPicker } = await import('./colorpicker/colorpicker');
        [elem, options] = await colorPicker(options);
        break;
      case 'editor':
        const { editor } = await import('./editor');
        [elem, options] = editor(options);
        break;
      case 'dialog':
        elem = document.createElement('div');
        options.dialog = {
          modal: options.isModal !== false,
          effect: "zoom",
          title: options.title || ' ',
          autoOpen: options.open !== false,
          kill: options.kill !== false,
          buttons: options.buttons
        };
        delete options.isModal;
        delete options.open;
        delete options.kill;
        delete options.buttons;
        delete options.title;
        break;
      case 'grid':
        const { default: grid } = await import('./grid');
        [elem, options] = grid(elem, options);
        break;
      case 'datagrid':
        await import('@ep/styles/components/layout/datagrid.scss');
        const datagridModule = await import('./datagrid/datagrid');
        const datagridFactory = datagridModule?.datagrid?.datagrid
          || datagridModule?.datagrid
          || datagridModule?.default?.datagrid?.datagrid
          || datagridModule?.default?.datagrid
          || datagridModule?.default
          || datagridModule;
        if (typeof datagridFactory !== 'function') {
          throw new Error('DATAGRID_MODULE_INVALID');
        }
        await datagridFactory(options);
        break;
      case 'board':
        const { default: board } = await import('./board/board');
        [elem, options] = board(elem, options);
        break;
      case 'boardColumnEditor':
        const { default: boardColumnEditor } = await import('./board/columnEditor');
        [elem, options] = boardColumnEditor(elem, options);
        break;
      case 'boardLayoutEditor':
        const { default: boardLayoutEditor } = await import('./board/layoutEditor');
        [elem, options] = boardLayoutEditor(elem, options);
        break;
      case 'boardStatusBreadcrumb':
        const { default: boardStatusBreadcrumb } = await import('./board/statusBreadcrumb');
        [elem, options] = await boardStatusBreadcrumb(elem, options);
        break;
      case 'group':
        const { default: group } = await import('./group');
        [elem, options] = await group(elem, options);
        break;
      case 'inputGroup': elem = await this.#createInputGroup(options); break;
      case 'optionList':
        const { default: optionList } = await import('./optionList');
        [elem, options] = await optionList(options);
        break;
      case 'json':
        const { json } = await import('./json');
        [elem, options] = json(options);
        break;
      case 'image':
        const { image } = await import('./image');
        await image(options);
        break;
      case 'iconPicker':
        const { default: iconPicker } = await import('./iconPicker');
        [elem, options] = await iconPicker(options);
        break;
      case 'knob':
        const { default: knob } = await import('./knob');
        knob(options);
        break;
      case 'list':
        const { default: list } = await import('./list');
        [elem, options] = list(elem, options);
        break;
      case 'login':
        const { default: login } = await import('./login');
        await login(options);
        break;
      case 'menu':
        const { default: menu } = await import('./menu');
        await menu(options);
        break;
      case 'miniDisplay':
        const { default: miniDisplay } = await import('./miniDisplay');
        elem = miniDisplay(options);
        break;
      case 'metricDisplay':
        const { default: metricDisplay } = await import('./metricDisplay');
        elem = metricDisplay(options);
        break;
      case 'multiList':
        const { default: multiList } = await import('./multiList');
        [elem, options] = await multiList(options);
        break;
      case 'otp':
        const { default: otp } = await import('./otp');
        elem = otp(options);
        break;
      case 'progress':
        const { default: progress } = await import('./progress');
        progress(options);
        break;
      case 'rating':
        const { default: rating } = await import('./rating');
        [elem, options] = rating(options);
        break;
      case 'tag':
        const { default: tag } = await import('./tag');
        [elem, options] = tag(elem, options);
        break;
      case 'textarea':
        const { textarea } = await import('./editor');
        [elem, options] = textarea(options);
        break;
      case 'treeList':
        const { treeList } = await import('./treeList');
        [elem, options] = await treeList(options);
        break;
      case 'upload':
        const { default: Upload } = await import('./upload');
        const u = new Upload(options);
        break;
      default:
        elem = document.createElement(options.tag);
    }

    if (elem) {
      append(elem, options);

      if (typeof options.helper === 'string' && options.helper !== '') {
        appendFieldHelper(elem, options.helper);
      }

      elem = setAttributes(elem, options);
      elem = await setMask(elem, options);
      elem = setEvents(elem, options);

      if (
        options.icon !== undefined
        && typeof options.icon === 'object'
        && options.icon.type !== undefined
        && !['checkbox', 'radio'].includes(options.element)
        && options.element !== 'tagbox'
        && !(options.element === 'combo' && (options.appearance === 'button' || options.type === 'button'))
      ) {
        setIcon(elem, options);
      }

      document.querySelectorAll('button.pfx, button.sfx').forEach(item => {
        item.setAttribute('tabindex', '-1');
      });

      if (options.element === 'dialog' || options.dialog) {
        elem.dialog(options.dialog);
      }

      const els = ['checkbox', 'radio'];
      const checks = ['cep', 'cnpj', 'cpf', 'creditCard'];
      if (els.includes(options.element)) {
        await this.#setCheckRadio(elem, options);
      } else if (checks.includes(options.element)) {
        let attempts = 0;
        /** Executa validação inicial quando os complementos assíncronos estiverem prontos. */
        const runInitialCheck = () => {
          if (!elem) return;

          // Credit-card validation needs the suffix flag created by addSuffix.
          // Retry briefly while the asynchronous component factory inserts it.
          const cardSuffixReady = options.element !== 'creditCard'
            || elem.nextElementSibling?.classList.contains('sfx');

          if (!cardSuffixReady && attempts++ < 20) {
            setTimeout(runInitialCheck, 25);
            return;
          }

          elem.dispatchEvent(new Event('blur'));
        };

        setTimeout(runInitialCheck, 0);
      } else if (options.element === 'phone') {
        const { setPhone } = await import('./phone');
        await setPhone(elem, options);
      } else if (options.element === 'slider') {
        sliderTicks(elem, options);
      } else if (options.element === 'list') {
        this.#setList(elem, options);
      } else if (options.element === 'editor') {
        const { default: Quill } = await import('quill');
        await import('quill/dist/quill.snow.css');
        const quill = new Quill(elem, options.quill);
        const wrapper = elem.parentElement;
        const colorLabels = options.quill?.colorLabels && typeof options.quill.colorLabels === 'object'
          ? options.quill.colorLabels
          : {};
        const hiddenFieldName = options.name || options.id;
        const hiddenFieldId = hiddenFieldName ? `${hiddenFieldName}-hidden` : null;
        const hiddenField = document.createElement('textarea');

        /** Aplica labels localizados aos swatches de cor do Quill. */
        const applyQuillColorLabels = () => {
          if (!wrapper) return;

          wrapper.querySelectorAll('.ql-color .ql-picker-item, .ql-background .ql-picker-item').forEach((item) => {
            const rawValue = String(item.getAttribute('data-value') || '').trim().toLowerCase();
            const label = colorLabels[rawValue];
            if (!label) return;
            item.setAttribute('title', label);
            item.setAttribute('aria-label', label);
          });

          wrapper.querySelectorAll('.ql-color .ql-picker-label, .ql-background .ql-picker-label').forEach((labelElem) => {
            const selectedValue = String(labelElem.getAttribute('data-value') || '').trim().toLowerCase();
            const label = colorLabels[selectedValue];
            if (!label) return;
            labelElem.setAttribute('title', label);
            labelElem.setAttribute('aria-label', label);
          });
        };

        hiddenField.name = hiddenFieldName;
        if (hiddenFieldId) {
          hiddenField.id = hiddenFieldId;
        }
        hiddenField.hidden = true;
        hiddenField.tabIndex = -1;
        hiddenField.classList.add('editor-value');
        if (options.required === true) {
          hiddenField.required = true;
        }
        if (options.readOnly === true) {
          hiddenField.readOnly = true;
        }
        if (options.disabled === true) {
          hiddenField.disabled = true;
        }

        /** Sincroniza o HTML normalizado do Quill no campo persistível oculto. */
        const syncEditorValue = () => {
          const plainText = (quill.getText() || '').trim();
          hiddenField.value = plainText.length > 0 ? this.#normalizeQuillHtml(quill.root.innerHTML) : '';
        };

        if (wrapper) {
          wrapper.classList.add('editor');
          wrapper.append(hiddenField);
        }

        syncEditorValue();
        applyQuillColorLabels();
        requestAnimationFrame(applyQuillColorLabels);
        quill.on('text-change', syncEditorValue);
        quill.on('editor-change', applyQuillColorLabels);
        this.#setupEditorCharacterCounter({ elem, wrapper, hiddenField, quill, options });

        quill.on('selection-change', (range) => {
          if (!wrapper) return;
          wrapper.classList.toggle('focus', Boolean(range));
          elem.classList.toggle('is-focused', Boolean(range));
        });
      } else if (options.element === 'json') {
        const { initializeJsonEditor } = await import('./json');
        await initializeJsonEditor(elem, options);
      } else if (options.element === 'combo') {
        const cbid = elem.dataset.opts;
        const opts = elem.querySelector(`[data-id="${cbid}"]`) || document.querySelector(`[data-id="${cbid}"]`);
        const rect = elem.getBoundingClientRect();
        let afterLabel;

        if (!opts || !elem.isConnected) {
          if (options.afterLabel) {
            afterLabel = document.createElement('div');
            afterLabel.textContent = options.afterLabel;
            afterLabel.classList.add('after-label');
            elem.after(afterLabel);
          }

          return elem;
        }

        opts.style.visibility = 'hidden';
        opts.style.position = 'absolute';
        opts.style.left = `${rect.left}px`;
        opts.style.top = `${rect.bottom}px`;
        document.body.append(opts)
        const qty = opts.querySelectorAll('div');
        const size = opts.data('size');
        const originalDisplay = opts.style.display;
        const scrolld = (qty.length > size) ? (17) : (0);
        opts.style.display = 'block';
        const optsWidth = opts.offsetWidth + 5 + scrolld;
        opts.style.visibility = 'hidden';
        const tempWidth = elem.offsetWidth;
        const elemWidth = optsWidth > tempWidth ? optsWidth : tempWidth;
        opts.style.display = originalDisplay;
        opts.style.visibility = 'visible';
        if (options.fitContent === true) {
          elem.style.width = 'fit-content';
          elem.style.minWidth = options.multiple === true ? '0' : 'fit-content';
        } else {
          elem.style.width = elemWidth + 'px';
        }

        if (options.afterLabel) {
          afterLabel = document.createElement('div');
          afterLabel.textContent = options.afterLabel;
          afterLabel.classList.add('after-label');
          elem.after(afterLabel);
        }

      } else if (options.element === 'group') {
        if (elem.classList.contains('accordion-group')) {
          const item = elem.querySelectorAll('.accordion-item')[options.active];
          const header = item?.querySelector('.accordion-header').click();
        }
        if (elem.classList.contains('tab-group')) {
          // O Group já define a tab ativa e a visibilidade inicial de forma síncrona.
          // Não use polling nem clique sintético: isso força layout de tabs ocultas.
        }

      }

      if (options.element === 'textarea') {
        this.#setupTextareaCharacterCounter(elem, options);
      }
    }

    if (options.content && Array.isArray(options.content) && options.content.length > 0) {
      let maxCol = 0, maxRow = 0;
      let parent;
      const shouldUseAutoGrid = this.#shouldUseAutoGrid(options);

      if (options.element === 'form') {
        parent = document.createElement('div');
        elem.append(parent);
      } else {
        parent = elem;
      }

      if (parent && shouldUseAutoGrid) {
        parent.classList.add('grid');
      }

      for (const item of options.content) {
        item.parent = parent;
        if (options.__dashboardContext && !item.__dashboardContext) {
          item.__dashboardContext = options.__dashboardContext;
        }

        // Valida coordenadas e preserva spans no Grid declarativo.
        const col = Number.isFinite(Number(item.col)) ? Math.max(0, Math.trunc(Number(item.col))) : 0;
        const row = Number.isFinite(Number(item.row)) ? Math.max(0, Math.trunc(Number(item.row))) : 0;
        const colspan = Number.isFinite(Number(item.colspan)) ? Math.max(1, Math.trunc(Number(item.colspan))) : 1;
        const rowspan = Number.isFinite(Number(item.rowspan)) ? Math.max(1, Math.trunc(Number(item.rowspan))) : 1;
        const hasPlacement = item.col !== undefined
          || item.row !== undefined
          || item.colspan !== undefined
          || item.rowspan !== undefined;
        if (hasPlacement) {
          const itemStyle = item.style && typeof item.style === "object" && !Array.isArray(item.style)
            ? { ...item.style }
            : {};
          itemStyle.gridColumn = colspan > 1 ? String(col + 1) + " / span " + String(colspan) : String(col + 1);
          itemStyle.gridRow = rowspan > 1 ? String(row + 1) + " / span " + String(rowspan) : String(row + 1);
          item.style = itemStyle;
          maxCol = Math.max(maxCol, col + colspan - 1);
          maxRow = Math.max(maxRow, row + rowspan - 1);
        }
      }

      const numCols = options.cols ?? maxCol + 1;
      const numRows = options.rows ?? maxRow + 1;

      if (parent && shouldUseAutoGrid) {
        // Define as colunas e linhas do grid
        parent.style.gridTemplateColumns = `repeat(${numCols}, 1fr)`;
        parent.style.gridTemplateRows = `repeat(${numRows}, auto)`;
      }

      // Remove `cols` e `rows` de options, se presentes
      delete options.cols;
      delete options.rows;

      // Chama a função de criação
      await create(options.content);
    }


    return elem;
  }
}

const element = new Element();

/**
 * Factory central exportada para criar um elemento já normalizado pelo EP.
 *
 * @param {Object} options Configuração do elemento; veja {@link Element#createElement}.
 * @returns {Promise<HTMLElement|*>} Resultado de `Element#createElement`.
 * @see Element#createElement
 */
const createElement = (options) => element.createElement(options);
export default createElement;
